import { useScroll } from "ahooks";
import { useMemo } from "react";

import { BaseLink } from "~/components/common/base-link";
import { IdleQueue } from "~/components/common/idle-queue";
import { HeaderContentBookmark } from "~/components/header/header-content-bookmark";
import { HeaderContentCommentTrigger } from "~/components/header/header-content-comment-trigger";
import { HeaderContentShareWidgetDrawer } from "~/components/header/header-content-share-widget-drawer";
import type {
  Feature,
  HeaderConfiguration,
  ResponsiveLogoVariant,
} from "~/components/header/types";
import { DynamicHeaderAvatar } from "~/components/home/header/dynamics";
import { HeaderMyNews } from "~/components/home/header/header-my-news";
import {
  ActionButton,
  AvatarContainer,
  StyledIconSearch,
  StyleSearchLink,
} from "~/components/home/header/styles";
import type { ActionButtonProps, LogoProps } from "~/components/home/header/styles";
import type {
  FeatureConfiguration,
  HomeResponsiveFeature,
  PositionsConfiguration,
  PositionVariant,
} from "~/components/home/header/types";
import { tracking } from "~/data";
import { useCurrentPageType } from "~/lib/router/hooks";
import { DynamicSubscriptionButton } from "~/lib/sdk/dynamics";
import type { OptionalBreakpointKey, RequiredBreakpointKey } from "~/lib/styles/responsive";
import { page as SearchPage } from "~/pages/search";
export const useSwapOnScrollEffect = (threshold: number) => {
  const { top } = useScroll() ?? { top: 0 };
  return { shouldSwap: top >= threshold };
};

export const useHomeHeader = (shouldSwap: boolean, headerConfiguration?: HeaderConfiguration) => {
  const featureConfigurations = useMemo(() => {
    // If not provided, use default responsive features
    const homeResponsiveFeatures: HomeResponsiveFeature = {
      desktopUp: {
        default: { left: ["my-news"], right: ["subscription", "dashboard"] },
      },
      homeDesktopUp: {
        default: {
          left: ["search", "my-news"],
          right: ["subscription", "dashboard"],
        },
      },
      mobileUp: {
        default: { left: [], right: ["dashboard"] },
      },
      tabletUp: {
        default: { left: ["my-news"], right: ["subscription", "dashboard"] },
      },
      ...headerConfiguration?.homeResponsiveFeatures,
    };

    const computeFeatureConfiguration = (feature: Feature): PositionsConfiguration => {
      const defaultFeatureConfig: FeatureConfiguration = {
        isEnabled: false,
        responsiveVariants: {
          desktopUp: "hidden",
          homeDesktopUp: "hidden",
          mobileUp: "hidden",
          tabletUp: "hidden",
        },
      };

      const positions: PositionsConfiguration = {
        left: {
          ...defaultFeatureConfig,
        },
        right: {
          ...defaultFeatureConfig,
        },
      };

      Object.entries(positions).map(([position]) => {
        const computeFeatureListForBreakpoint = (
          breakpoint: RequiredBreakpointKey | OptionalBreakpointKey,
        ) => {
          const featureForBreakpoint = homeResponsiveFeatures[breakpoint];
          if (!featureForBreakpoint) return [];
          const features = shouldSwap
            ? featureForBreakpoint.swapped ?? featureForBreakpoint.default
            : featureForBreakpoint.default;
          return features[position as keyof PositionsConfiguration];
        };

        const responsiveVariants: ActionButtonProps["$responsiveVariants"] = {
          desktopUp: computeFeatureListForBreakpoint("desktopUp").includes(feature)
            ? "show"
            : "hidden",
          homeDesktopUp: computeFeatureListForBreakpoint("homeDesktopUp").includes(feature)
            ? "show"
            : "hidden",
          mobileUp: computeFeatureListForBreakpoint("mobileUp").includes(feature)
            ? "show"
            : "hidden",
          tabletUp: computeFeatureListForBreakpoint("tabletUp").includes(feature)
            ? "show"
            : "hidden",
        };

        const isEnabled = Object.values(responsiveVariants).includes("show");

        positions[position as keyof PositionsConfiguration] = {
          isEnabled,
          responsiveVariants,
        };
      });

      return positions;
    };

    const subscription = computeFeatureConfiguration("subscription");
    const dashboard = computeFeatureConfiguration("dashboard");
    const search = computeFeatureConfiguration("search");
    const myNews = computeFeatureConfiguration("my-news");
    const contentShare = computeFeatureConfiguration("content-share");
    const contentBookmark = computeFeatureConfiguration("content-bookmark");
    const contentComment = computeFeatureConfiguration("content-comment");

    return {
      contentBookmark,
      contentComment,
      contentShare,
      dashboard,
      myNews,
      search,
      subscription,
    };
  }, [headerConfiguration, shouldSwap]);

  const currentPageType = useCurrentPageType();
  const mastheadQueryParameter = {
    module: tracking.module.Masthead,
    pgtype: currentPageType,
  };

  const renderFeatures = (
    position: PositionVariant,
    avatarId?: string,
  ): undefined | JSX.Element => {
    if (!featureConfigurations) return;
    const {
      contentBookmark,
      contentComment,
      contentShare,
      dashboard,
      myNews,
      search,
      subscription,
    } = featureConfigurations;

    const { pathname: searchPathname, query: searchQuery } = SearchPage.route({});

    return (
      <>
        <IdleQueue>
          {subscription[position].isEnabled && (
            <ActionButton $responsiveVariants={subscription[position].responsiveVariants}>
              <DynamicSubscriptionButton
                variant={
                  headerConfiguration?.appBarVariant === "scmp/magazines-style"
                    ? "dark"
                    : "light-blue"
                }
              />
            </ActionButton>
          )}
        </IdleQueue>

        {search[position].isEnabled && (
          <ActionButton $responsiveVariants={search[position].responsiveVariants}>
            <StyleSearchLink
              anchorProps={{ "aria-label": "search" }}
              as="/search"
              pathname={searchPathname}
              query={{ ...searchQuery, ...mastheadQueryParameter }}
            >
              <StyledIconSearch />
            </StyleSearchLink>
          </ActionButton>
        )}

        {myNews[position].isEnabled && (
          <ActionButton $responsiveVariants={myNews[position].responsiveVariants}>
            <BaseLink
              anchorProps={{ "aria-label": "my news" }}
              as="/mynews"
              pathname="mynews"
              query={mastheadQueryParameter}
            >
              <HeaderMyNews />
            </BaseLink>
          </ActionButton>
        )}

        {contentComment[position].isEnabled && (
          <ActionButton $responsiveVariants={contentComment[position].responsiveVariants}>
            <HeaderContentCommentTrigger />
          </ActionButton>
        )}
        {contentShare[position].isEnabled && (
          <ActionButton $responsiveVariants={contentShare[position].responsiveVariants}>
            <HeaderContentShareWidgetDrawer />
          </ActionButton>
        )}
        {contentBookmark[position].isEnabled && (
          <ActionButton $responsiveVariants={contentBookmark[position].responsiveVariants}>
            <HeaderContentBookmark />
          </ActionButton>
        )}
        {dashboard[position].isEnabled && (
          <ActionButton $responsiveVariants={dashboard[position].responsiveVariants}>
            <AvatarContainer>
              <IdleQueue>
                <DynamicHeaderAvatar avatarId={avatarId} />
              </IdleQueue>
            </AvatarContainer>
          </ActionButton>
        )}
      </>
    );
  };

  const responsiveLogoVariant = useMemo(() => {
    // If not provided, use default responsive logo variant
    const responsiveLogoVariant: ResponsiveLogoVariant = {
      desktopUp: {
        default: "logo",
      },
      homeDesktopUp: {
        default: "logo",
      },
      mobileUp: {
        default: "full",
      },
      tabletUp: {
        default: "full",
      },
      ...headerConfiguration?.responsiveLogoVariant,
    };

    const computeVariantForBreakpoint = (
      breakpoint: RequiredBreakpointKey | OptionalBreakpointKey,
    ) => {
      const variantForBreakpoint = responsiveLogoVariant[breakpoint];
      if (!variantForBreakpoint) return "full";
      const features = shouldSwap
        ? variantForBreakpoint.swapped ?? variantForBreakpoint.default
        : variantForBreakpoint.default;
      return features;
    };

    const responsiveVariants: LogoProps["$responsiveVariants"] = {
      desktopUp: computeVariantForBreakpoint("desktopUp"),
      homeDesktopUp: computeVariantForBreakpoint("homeDesktopUp"),
      mobileUp: computeVariantForBreakpoint("mobileUp"),
      tabletUp: computeVariantForBreakpoint("tabletUp"),
    };

    return responsiveVariants;
  }, [headerConfiguration, shouldSwap]);

  return {
    featureConfigurations,
    mastheadQueryParameter,
    renderFeatures,
    responsiveLogoVariant,
  };
};
